// =====================================================
// lib/feedHelpers.ts
// Types partagés, helpers et getValidToken
// Importé par actu.tsx et tous ses sous-composants
// =====================================================

import { Platform }   from 'react-native';
import AsyncStorage    from '@react-native-async-storage/async-storage';

// ─── Config ───────────────────────────────────────────────────────────────────
export const BACKEND_URL  = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
export const HOME_URL     = `${BACKEND_URL}/home`;
export const PROFILE_URL  = `${BACKEND_URL}/profile`;
export const NATIVE       = Platform.OS !== 'web';

// ─── Couleurs ─────────────────────────────────────────────────────────────────
export const LIKE_ACTIVE_COLOR   = '#8A2BE2';
export const LIKE_INACTIVE_COLOR = '#65676B';
export const FB_GRAY_COLOR       = '#65676B';

// ─── Types ────────────────────────────────────────────────────────────────────
export type FilterMode = 'all' | 'posts' | 'images' | 'videos' | 'music';
export type GameType   = 'arts' | 'performance' | 'music' | 'artisanat';
export type Visibility = 'public' | 'friends' | 'private' | 'pro';
export type ToastType  = 'success' | 'error' | 'info';

export interface Post {
  item_type:    'post';
  id:           string;
  author_id:    string;
  author:       { nom: string; prenom: string; avatar_url: string | null };
  content:      string;
  visibility:   string;
  created_at:   string;
  imagepots?:   string | null;
  vidposts?:    string | null;
  reactions:    { likes: number; comments: number; shares: number };
  user_liked?:  boolean;
  user_shared?: boolean;
  user_saved?:  boolean;
}

export interface SubmissionMedia {
  id: string; url: string; description?: string | null;
}

export interface Submission {
  item_type:   'submission';
  game_type:   GameType;
  badge:       string;
  id:          string;
  run_id:      string;
  session_id:  string;
  user_id:     string;
  run_number:  number;
  run_title:   string;
  author:      { nom: string; prenom: string; avatar_url: string | null };
  media:       SubmissionMedia[];
  total_votes: number;
  user_voted:  boolean;
  created_at:  string;
}

export type FeedItem = Post | Submission;

export interface UserProfile   { solde_cfa: number; trophies_count: number; role?: string }
export interface MyProfile     { id: string; nom: string; prenom: string; avatar_url?: string; role: string }

export interface VideoFeedItem {
  mediaId: string; videoUrl: string; description: string | null;
  author: { nom: string; prenom: string; avatar_url: string | null };
  run_number: number; run_title: string; session_id: string;
  total_votes: number; user_voted: boolean; game_type: GameType;
  run_id: string; user_id: string;
}

export interface AudioFeedItem {
  mediaId: string; audioUrl: string; description: string | null;
  author: { nom: string; prenom: string; avatar_url: string | null };
  run_number: number; run_title: string; session_id: string;
  total_votes: number; user_voted: boolean; game_type: GameType;
  run_id: string; user_id: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
export const formatTimeAgo = (dateString: string): string => {
  const diff = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (diff < 60)    return "À l'instant";
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
};

export const getInitials = (nom?: string | null, prenom?: string | null): string => {
  const n = (nom    || '').trim();
  const p = (prenom || '').trim();
  return `${p.charAt(0)}${n.charAt(0)}`.toUpperCase() || '?';
};

export const uriToBlob = (uri: string): Promise<Blob> =>
  new Promise((resolve, reject) => {
    if (Platform.OS === 'web') {
      fetch(uri).then(r => r.blob()).then(resolve).catch(reject);
    } else {
      const xhr = new XMLHttpRequest();
      xhr.onload  = () => resolve(xhr.response);
      xhr.onerror = () => reject(new Error('Blob échoué'));
      xhr.responseType = 'blob';
      xhr.open('GET', uri, true);
      xhr.send(null);
    }
  });

export const uploadToStorage = async (signedUrl: string, uri: string): Promise<void> => {
  const blob        = await uriToBlob(uri);
  const contentType = blob.type || 'application/octet-stream';
  const uploadRes   = await fetch(signedUrl, {
    method: 'PUT', headers: { 'Content-Type': contentType }, body: blob,
  });
  if (!uploadRes.ok) {
    const errText = await uploadRes.text().catch(() => '');
    throw new Error(`Échec upload (${uploadRes.status}) ${errText}`);
  }
};

export async function getValidToken(): Promise<{
  uid: string; token: string; refresh_token: string; expires_at: number;
} | null> {
  try {
    const raw = await AsyncStorage.getItem('harmonia_session');
    if (!raw) return null;
    const session      = JSON.parse(raw);
    const uid          = session?.user?.id      || '';
    const accessToken  = session?.access_token  || '';
    const refreshToken = session?.refresh_token || '';
    const expiresAt    = session?.expires_at    || 0;
    if (!uid || !accessToken) return null;

    const now = Math.floor(Date.now() / 1000);
    if (expiresAt - now > 60) return { uid, token: accessToken, refresh_token: refreshToken, expires_at: expiresAt };
    if (!refreshToken) return null;

    const res = await fetch(`${BACKEND_URL}/refresh-token`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return null;
    const data           = await res.json();
    const updatedSession = {
      ...session,
      access_token:  data.access_token,
      refresh_token: data.refresh_token,
      expires_at:    data.expires_at,
    };
    await AsyncStorage.setItem('harmonia_session', JSON.stringify(updatedSession));
    return { uid, token: data.access_token, refresh_token: data.refresh_token, expires_at: data.expires_at };
  } catch { return null; }
  }
